/**
 * OverrideModal - Safe deselection modal for required equipment items
 * Allows operators to override regulatory requirements with audit trail
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';

const OverrideModal = ({ item, domain, onConfirm, onCancel }) => {
  const [reasonCode, setReasonCode] = useState('');
  const [details, setDetails] = useState('');
  const [evidence, setEvidence] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!reasonCode || !details) {
      alert('Please select a reason and provide details');
      return;
    }

    onConfirm({
      reasonCode,
      details,
      evidence: evidence ? [{ type: 'url', value: evidence }] : [],
      expiresAt: expiresAt || undefined
    });
  };

  const reasonOptions = [
    { value: 'dispensation', label: 'Dispensation / Exemption' },
    { value: 'equivalent', label: 'Equivalent Arrangement (Flag/Class approved)' },
    { value: 'not_applicable', label: 'Not Applicable to this vessel' },
    { value: 'temporary', label: 'Temporarily Removed for Service' },
    { value: 'other', label: 'Other (specify in details)' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onCancel} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <form onSubmit={handleSubmit} className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Override Requirement</h3>
                <p className="text-sm text-gray-600 mt-1">{item.name}</p>
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Reason Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Override *
              </label>
              <div className="space-y-2">
                {reasonOptions.map(option => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="radio"
                      name="reason"
                      value={option.value}
                      checked={reasonCode === option.value}
                      onChange={(e) => setReasonCode(e.target.value)}
                      className="h-4 w-4 text-cyan-600 border-gray-300 focus:ring-cyan-500"
                    />
                    <span className="ml-2 text-sm text-gray-900">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Details */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Details *
              </label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Provide details about the override..."
                required
              />
            </div>

            {/* Evidence (Optional) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Evidence (Optional)
              </label>
              <input
                type="url"
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Link to supporting documentation..."
              />
            </div>

            {/* Expiry Date (Optional) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date (Optional)
              </label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                If temporary, add an expiry date so we'll flag it when the exemption ends.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
              >
                Record Override
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OverrideModal;